import React, {useEffect, useState} from 'react';
import ReactDatepicker from 'react-datepicker';
import ReactSelect from 'react-select/creatable';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {Choice} from '../types';
import {WorkflowSearchFormFieldFilter} from './WorkflowSearchFormFieldFilter';

type WorkflowSearchFormProps = {
    selectedWorkflowId?: string;
    onSearch: (formValues: any) => void;
    onChange?: (state: WorkflowSearchFormState) => void;
    formState: WorkflowSearchFormState;
    setFormState: (state: WorkflowSearchFormState) => void;
};

export type WorkflowSearchFormState = {
    workflowId: string;
    startDate: Date | null;
    startDateText?: string;
    endDate: Date | null;
    endDateText?: string;
    workflow: Choice | {};
    fieldFilters: object[];
};

export const initialFormState: WorkflowSearchFormState = {
    workflowId: '',
    startDate: null,
    startDateText: '',
    endDate: null,
    endDateText: '',
    workflow: {},
    fieldFilters: [{}]
};

export const WorkflowSearchForm: React.FunctionComponent<WorkflowSearchFormProps> = ({
                                                                                         selectedWorkflowId = '',
                                                                                         onSearch,
                                                                                         onChange,
                                                                                         formState,
                                                                                         setFormState,
                                                                                     }) => {
    const [searchFilters, setSearchFilters] = useState([]);
    const [searchFiltersIsLoading, setSearchFiltersIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setSearchFiltersIsLoading(true);
        Flow.getInstance().getSearchFilters()
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setSearchFilters(response.json.filters);

                    // if passed a workflow, update the form state
                    selectedWorkflowId && response.json.filters.some((filter: any) => {
                        if (String(filter.id) === selectedWorkflowId) {
                            setFormState({
                                ...formState,
                                workflow: {
                                    ...filter,
                                    value: filter.id,
                                    label: filter.title
                                }
                            });
                            return true;
                        }
                        return false;
                    });
                }
                setSearchFiltersIsLoading(false);
            })
            .catch(() => {
                setSearchFiltersIsLoading(false);
            });
    }, [false]);

    useEffect(() => {
        searchFilters.some((filter: any) => {
            if (String(filter.id) === selectedWorkflowId) {
                setFormState({
                    ...formState,
                    fieldFilters: [{}],
                    workflow: {
                        ...filter,
                        value: filter.id,
                        label: filter.title
                    }
                });
                return true;
            }
            return false;
        });
    }, [selectedWorkflowId]);

    return (
        <form id="workflow-search-form"
              className="form-inline p-3"
        >
            <input type="text"
                   placeholder="ID"
                   className="workflow-id form-control"
                   value={formState?.workflowId || ''}
                   onChange={(e) => {
                       setFormState({...formState, workflowId: e.target.value})
                   }}
                   onKeyDown={(e) => {
                       if (e.key === 'Enter') {
                           onSearch && onSearch(formState);
                       }
                   }}
            />
            <label className="pl-4 pr-2">Start:</label>
            <ReactDatepicker
                className="form-control"
                selected={formState?.startDate || null}
                onChange={(selected) => {
                    setFormState({
                        ...formState,
                        startDate: selected,
                        startDateText: selected?.toISOString().slice(0, 10)
                    })
                    onChange && onChange(formState);
                }}
            />
            <label className="pl-4 pr-2">End:</label>
            <ReactDatepicker
                className="form-control"
                selected={formState?.endDate || null}
                onChange={(selected) => {
                    setFormState({
                        ...formState,
                        endDate: selected,
                        endDateText: selected?.toISOString().slice(0, 10)
                    });
                    onChange && onChange(formState);
                }}
            />

            {searchFiltersIsLoading && <i className="fas fa-spinner fa-spin ml-4"/>}

            {searchFilters?.length > 0 && !selectedWorkflowId &&
            <div className="pl-4 react-select-fixed-width">
                <ReactSelect
                    placeholder="Select a Workflow"
                    isSearchable={true}
                    isClearable={true}
                    options={searchFilters.map((filter: any) => ({
                        ...filter,
                        value: filter.id,
                        label: filter.title
                    }))}
                    onChange={(option: any, type: any) => {
                        if (type.action === 'clear') {
                            setFormState({...formState, workflow: {}});
                        } else {
                            option && setFormState({...formState, workflow: option})
                        }
                    }}
                    value={formState?.workflow}
                />
            </div>
            }

            <button type="button"
                    className="btn btn-secondary ml-4"
                    onClick={() => {
                        onSearch && onSearch(formState);
                    }
                    }>Search
            </button>

            <button type="button"
                    className="btn btn-secondary ml-2"
                    onClick={() => {
                        setFormState({
                            ...initialFormState,
                            workflow: {...formState.workflow}
                        });
                        onSearch && onSearch(initialFormState);
                    }
                    }>Clear
            </button>

            <div className="flex-line-break"/>
            {
                // @ts-ignore
                formState?.workflow?.id &&
                <div>
                    {formState.fieldFilters.map((fieldFilter: any, index: number) => (
                        <WorkflowSearchFormFieldFilter
                            key={index}
                            // @ts-ignore
                            filters={formState?.workflow?.filters}
                            operators={fieldFilter?.workflowField?.operators}
                            values={fieldFilter?.workflowField?.values}
                            filterValues={fieldFilter}
                            onChange={(field: string, value: any) => {
                                setFormState({
                                    ...formState,
                                    fieldFilters: [
                                        ...formState.fieldFilters.slice(0, index),
                                        {
                                            ...formState.fieldFilters[index],
                                            [field]: value
                                        },
                                        ...formState.fieldFilters.slice(index + 1)
                                    ],
                                });
                                onChange && onChange(formState);
                            }}
                        />
                    ))}

                    <div className="d-block mt-2">
                        <button
                            type="button"
                            className="btn btn-light text-muted"
                            onClick={() => {
                                setFormState({
                                    ...formState,
                                    fieldFilters: [
                                        ...formState.fieldFilters,
                                        {}
                                    ]
                                })
                            }}
                        >
                            <i className="fas fa-plus"/>
                            <span className="pl-3">Add Filter</span>
                        </button>
                    </div>
                </div>
            }
        </form>
    );
};